import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { UxError } from 'src/app/models/ux-error.model';
import {
  RecentChangeItem,
  RecentChangesResponse,
} from 'src/app/models/home/recent-changes/recent-changes.model';
import { LanguageService } from 'src/app/services/language/language.service';

/**
 * Home IA · Radar de cambios recientes (R5 · ADR-R5-04). Widget read-only bajo el fold: lista el
 * top-N (max 5) de cambios documentales de severidad relevante (alta/critica) con badge de
 * severidad, resumen, fecha y deep-link a `/changes`. Es un RADAR de cambios, no un buzon de
 * alertas: no confirma, descarta ni notifica nada (eso vive en `/changes`). Se oculta por completo
 * si el plan no tiene `change_detection` (featureAvailable=false). Degrada de forma aislada:
 * loading/empty/error propios que nunca tumban la decision protagonista.
 */
@Component({
  selector: 'app-home-recent-changes-widget',
  templateUrl: './home-recent-changes-widget.component.html',
  styleUrls: ['./home-recent-changes-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class HomeRecentChangesWidgetComponent {
  @Input() response: RecentChangesResponse | null = null;
  @Input() loading = false;
  @Input() error: UxError | null = null;
  @Output() retryRequest = new EventEmitter<void>();

  readonly changesLink = '/changes';
  private readonly maxItems = 5;

  constructor(public lang: LanguageService) {}

  get featureAvailable(): boolean {
    return this.response?.featureAvailable ?? false;
  }

  get items(): RecentChangeItem[] {
    return (this.response?.items ?? []).slice(0, this.maxItems);
  }

  /** Error si falla la llamada entera O si el agregador marca la seccion como `error`. */
  get hasError(): boolean {
    return !!this.error || this.response?.status === 'error';
  }

  get isEmpty(): boolean {
    return !this.loading && !this.hasError && (this.response?.status === 'empty' || this.items.length === 0);
  }

  severityLabel(item: RecentChangeItem): string {
    return this.lang.translate(`changes.severity.${item.severity}`);
  }

  trackById(_: number, item: RecentChangeItem): string {
    return item.id;
  }

  onRetry(): void {
    this.retryRequest.emit();
  }
}
